import { AsyncPipe } from '@angular/common'
import { ChangeDetectionStrategy, Component, inject } from '@angular/core'
import { map } from 'rxjs'
import { ConnectionState, EnvironmentFanOutService, EnvironmentResult } from '@app/multi-environment/environment-fan-out.service'
import { disconnectedEnvironments, rollCall } from '@app/multi-environment/environment-merge'
import { SupportEnvironment } from '@app/multi-environment/support-environment'

const REASONS: Record<ConnectionState, string> = {
  checking: '',
  connected: '',
  signInRequired: 'Your session here has expired',
  notPermitted: 'This account is not a support user here',
  unreachable: 'No response from this environment',
}

/**
 * Sits in the shell rather than on a page, because an environment that cannot be read is missing
 * from every screen Atlas has: saying so once, in the same place, is what makes it possible to trust
 * a page that shows no banner.
 */
@Component({
  selector: 'atlas-connection-banner',
  templateUrl: './connection-banner.component.html',
  styleUrl: './connection-banner.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AsyncPipe],
})
export class ConnectionBannerComponent {
  private fanOutService = inject(EnvironmentFanOutService)

  private blockedKeys: string[] = []

  readonly vm$ = this.fanOutService.connections$.pipe(
    map((connections) => ({ disconnected: disconnectedEnvironments(connections), rollCall: rollCall(connections) }))
  )

  reason(state: ConnectionState): string {
    return REASONS[state]
  }

  // Named against what is still on the banner, so that signing in to one environment takes it out of
  // the count without the reader having to press anything again.
  blockedAmong(disconnected: EnvironmentResult<unknown>[]): number {
    return disconnected.filter((result) => this.blockedKeys.includes(result.environment.key)).length
  }

  openAll(disconnected: EnvironmentResult<unknown>[]) {
    this.blockedKeys = disconnected.filter((result) => !this.open(result.environment)).map((result) => result.environment.key)
  }

  /**
   * Opened without `noopener` on purpose. With it the call returns null whether the tab opened or
   * not, and opening several at once is exactly what a pop-up blocker stops after the first — so the
   * handle is the only way to know there is something to tell the reader. Dropping the opener by
   * hand afterwards leaves the new tab no better off than `noopener` would have.
   */
  private open(environment: SupportEnvironment): boolean {
    const opened = window.open(`${environment.origin}/app`, '_blank')
    if (opened === null) {
      return false
    }
    opened.opener = null
    return true
  }
}
